import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Stack, Box, Paper, Chip, Divider } from '@mui/material';
import { Info, Agriculture, Public, Security } from '@mui/icons-material';
import { uhuzamlogo } from '../constants/logos';
import { modernDesignSystem } from '../constants/themes';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: modernDesignSystem.borders.radius.large,
    boxShadow: modernDesignSystem.shadows.heavy,
    background: `linear-gradient(135deg, ${modernDesignSystem.colors.neutral.white} 0%, ${modernDesignSystem.colors.neutral.gray50} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${modernDesignSystem.colors.neutral.gray200}`,
  },
  '& .MuiDialogContent-root': {
    padding: modernDesignSystem.spacing.lg,
    backgroundColor: 'transparent',
  },
  '& .MuiDialogTitle-root': {
    background: `linear-gradient(135deg, ${modernDesignSystem.colors.primary.main} 0%, ${modernDesignSystem.colors.secondary.main} 100%)`,
    color: modernDesignSystem.colors.neutral.white,
    borderRadius: `${modernDesignSystem.borders.radius.large} ${modernDesignSystem.borders.radius.large} 0 0`,
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle 
      sx={{ 
        m: 0, 
        p: modernDesignSystem.spacing.lg,
        position: 'relative',
        textAlign: 'center',
        fontSize: modernDesignSystem.typography.fontSize.xl,
        fontWeight: 700,
        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
        }
      }} 
      {...other}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
        <Agriculture sx={{ fontSize: '2rem' }} />
        {children}
      </Box>
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: modernDesignSystem.spacing.md,
            top: modernDesignSystem.spacing.md,
            color: modernDesignSystem.colors.neutral.white,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            zIndex: 2,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const About = (props) => {
  const visibleAbout = props.visible;
  const setVisibleAbout = props.visibleFunc;

  const handleClose = () => {
    setVisibleAbout(false);
  };

  return (
    <BootstrapDialog
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={visibleAbout}
    >
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      >
        UHUZAM TARSİS
      </BootstrapDialogTitle>
      
      <DialogContent>
        {/* Ana İçerik Alanı */}
        <Stack spacing={modernDesignSystem.spacing.lg}>
          
          {/* Proje Açıklaması */}
          <Paper
            elevation={0}
            sx={{
              padding: modernDesignSystem.spacing.lg,
              background: `linear-gradient(135deg, ${modernDesignSystem.colors.primary.surface} 0%, ${modernDesignSystem.colors.neutral.white} 100%)`,
              borderRadius: modernDesignSystem.borders.radius.medium,
              border: `1px solid ${modernDesignSystem.colors.primary.light}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: modernDesignSystem.spacing.md }}>
              <Info sx={{ color: modernDesignSystem.colors.primary.main, fontSize: '1.5rem' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: modernDesignSystem.colors.primary.dark,
                  fontSize: modernDesignSystem.typography.fontSize.lg,
                }}
              >
                Proje Hakkında
              </Typography>
            </Box>
            
            <Typography
              sx={{
                fontSize: modernDesignSystem.typography.fontSize.base,
                lineHeight: 1.6,
                color: modernDesignSystem.colors.neutral.gray800,
                textAlign: 'justify',
                marginBottom: modernDesignSystem.spacing.md,
              }}
            >
              UHUZAM TARSİS Web Tabanlı Coğrafi Bilgi Sistemi projesi, bilgi teknolojileri 
              kullanılarak katma değerli uydu görüntüsü ve vektör verilerin web servisleri 
              aracılığıyla kullanıcılara güvenli bir şekilde sunulması için hazırlanmıştır.
            </Typography>


          </Paper>


          {/* Logo Alanı */}
          <Paper
            elevation={0}
            sx={{
              padding: modernDesignSystem.spacing.lg,
              background: `linear-gradient(135deg, ${modernDesignSystem.colors.neutral.white} 0%, ${modernDesignSystem.colors.secondary.surface} 100%)`,
              borderRadius: modernDesignSystem.borders.radius.medium,
              border: `1px solid ${modernDesignSystem.colors.secondary.light}`,
              textAlign: 'center',
            }}
          >
            
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: modernDesignSystem.spacing.md,
                backgroundColor: modernDesignSystem.colors.neutral.white,
                borderRadius: modernDesignSystem.borders.radius.small,
                boxShadow: modernDesignSystem.shadows.small,
                border: `1px solid ${modernDesignSystem.colors.neutral.gray200}`,
              }}
            >
              <Box
                component="img"
                sx={{
                  height: 120,
                  width: 'auto',
                  maxWidth: '100%',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                }}
                src={uhuzamlogo}
                alt="UHUZAM Logo"
              />
            </Box>
          </Paper>

        </Stack>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default About;
